<template>
  <section class="property-listings py-5">
    <div class="container">
      <div class="row">
        <div class="col-12 text-center mb-4">
          <h3 class="section-title">Trending Properties</h3>
          <p class="text-muted">Discover your perfect home in Texas from our curated selection</p>
        </div>
      </div>
      
      <div class="row">
        <div class="col-12">
          <div id="propertyCarousel" class="carousel slide" data-bs-ride="carousel">
            <!-- Carousel Indicators -->
            <div class="carousel-indicators">
              <button 
                v-for="(slide, index) in propertySlides" 
                :key="index"
                type="button" 
                data-bs-target="#propertyCarousel" 
                :data-bs-slide-to="index"
                :class="{ active: index === 0 }"
                :aria-label="`Slide ${index + 1}`">
              </button>
            </div>

            <!-- Carousel Items -->
            <div class="carousel-inner">
              <div 
                v-for="(slide, slideIndex) in propertySlides" 
                :key="slideIndex"
                :class="['carousel-item', { active: slideIndex === 0 }]">
                <div class="row g-4">
                  <div 
                    v-for="property in slide" 
                    :key="property.id"
                    class="col-lg-4 col-md-6"
                    @click="goToMLSsite(property.url)">
                    <div class="property-card card h-100 shadow-sm">
                      <div class="position-relative">
                        <img 
                          v-if="property.imageSrc"
                          :src="property.imageSrc" 
                          :alt="property.title"
                          class="card-img-top property-image">
                        <span v-else>Loading {{ property.title }}...</span>
                        <div class="property-badge badge bg-primary position-absolute top-0 start-0 m-3">
                          {{ property.status }}
                        </div>
                        <div class="property-price position-absolute bottom-0 end-0 m-3">
                          <span class="badge bg-dark fs-6">${{ formatPrice(property.price) }}</span>
                        </div>
                      </div>
                      
                      <div class="card-body">
                        <h5 class="card-title">{{ property.title }}</h5>
                        <p class="card-text text-muted mb-2">
                          <i class="bi bi-geo-alt"></i> {{ property.location }}
                        </p>
                        
                        <div class="property-features d-flex justify-content-between mb-3">
                          <span class="feature">
                            <i class="bi bi-house"></i> {{ property.bedrooms }} bed
                          </span>
                          <span class="feature">
                            <i class="bi bi-droplet"></i> {{ property.bathrooms }} bath
                          </span>
                          <span class="feature">
                            <i class="bi bi-arrows-move"></i> {{ property.sqft }} sqft
                          </span>
                        </div>
                        
                        <button class="btn btn-primary w-100">View Details</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Carousel Controls -->
            <button class="carousel-control-prev" type="button" data-bs-target="#propertyCarousel" data-bs-slide="prev">
              <span class="carousel-control-prev-icon" aria-hidden="true"></span>
              <span class="visually-hidden">Previous</span>
            </button>
            <button class="carousel-control-next" type="button" data-bs-target="#propertyCarousel" data-bs-slide="next">
              <span class="carousel-control-next-icon" aria-hidden="true"></span>
              <span class="visually-hidden">Next</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed} from 'vue'
import jsonPropertyListings from '@/resources/propertyListings.json';

// Fetch properties data
const properties = ref([
  ...jsonPropertyListings
]);

properties.value = properties.value.map(item => ({
   ...item,
   imageSrc: null
}));

// Static import of all .jpeg files from listings folder
const images = require.context('@/assets/images/listings/', false, /\.jpeg$/);

// Immediately enrich each item with imageSrc
properties.value = properties.value.map(item => {
  const imageName = item.url.substring(item.url.lastIndexOf('/') + 1); //
  const path = `./${imageName}.jpeg`;

  return {
    ...item,
    imageSrc: images.keys().includes(path) ? images(path) : null
  };
});

// Group properties into slides (3 per slide)
const propertySlides = computed(() => {
  const slides = []
  for (let i = 0; i < properties.value.length; i += 3) {
    slides.push(properties.value.slice(i, i + 3))
  }
  return slides
})

// Format price with commas
const formatPrice = (price) => {
  return price.toLocaleString()
}

//go to listings site
const goToMLSsite = (url) => {
  window.location.href = url; // opens in the same tab
  // or use: window.open(url, '_blank');
}
</script>

<style scoped>
.property-listings {
  background-color: #f8f9fa;
}

.section-title {
  color: #2c3e50;
  font-weight: 600;
  margin-bottom: 1rem;
  caret-color: transparent;
}

.property-card {
  border: none;
  border-radius: 12px;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.property-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0,0,0,0.15) !important;
  cursor: pointer;
}

.property-image {
  height: 250px;
  object-fit: cover;
  width: 100%;
}

.property-badge {
  font-size: 0.75rem;
  padding: 0.5rem 0.75rem;
  border-radius: 20px;
}

.property-price .badge {
  font-size: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
}

.property-features {
  font-size: 0.9rem;
  color: #6c757d;
}

.property-features .feature {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.carousel-control-prev,
.carousel-control-next {
  width: 5%;
  color: #007bff;
}

.carousel-control-prev-icon,
.carousel-control-next-icon {
  background-color: rgba(0, 123, 255, 0.8);
  border-radius: 50%;
  width: 3rem;
  height: 3rem;
}

.carousel-indicators button {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin: 0 4px;
}

.carousel-indicators .active {
  background-color: #007bff;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .property-features {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }
  
  .carousel-control-prev,
  .carousel-control-next {
    width: 8%;
  }
}

@media (max-width: 576px) {
  .property-image {
    height: 200px;
  }
  
  .carousel-inner .row > div {
    margin-bottom: 1rem;
  }
}
</style>